import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Select } from '@/shared/ui/Select';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/Toast';
import { clsx } from 'clsx';
import { useQuery } from '@tanstack/react-query';
import {
  agencyApi,
  type AiActionResult,
  type Location,
  type SiteAuditStatus,
} from '../../api/agencyApi';
import { ModelOverridesModal } from '../ModelOverridesModal';
import { agencyKeys, commandCenterKeys } from '../../queryKeys';
import { practiceFeatureFlagKeys } from '@/shared/hooks/usePracticeFeatureFlags';
import styles from './AdminCards.module.css';

/**
 * Operator tooling for one practice: force a run, override a model, wipe a
 * feature's data.
 *
 * These used to sit in the middle of the practice detail page, between the
 * locations list and the team roster, which made the two things an admin
 * reaches for most — "re-crawl their site" and "which model is this prompt
 * using" — the hardest to find on the page. They live on their own tab now.
 */

type AiAction = { key: string; label: string; description: string };

const OUTCOME_LABEL: Record<AiActionResult['outcome'], string> = {
  queued: 'Queued',
  done: 'Done',
  noop: 'Nothing to do',
};

const AI_ACTIONS: AiAction[] = [
  {
    key: 'recrawl-site',
    label: 'Re-crawl website & re-audit',
    description:
      "Fetches the practice's website again from scratch, bypasses the cache, then re-scores it. Use this when they have changed their site and the audit is still reading the old copy. Runs in the background; results in ~5-10 minutes.",
  },
  {
    key: 'run-site-audit',
    label: 'Re-run site audit',
    description:
      'Re-scores the site against the pages already crawled, without fetching it again. Cheaper and faster than a re-crawl — use it after fixes have been deployed.',
  },
  {
    key: 'recommend-campaigns',
    label: 'Generate Campaign Recommendations',
    description: 'AI analyzes practice context and strategy to create 2-3 campaign briefs',
  },
  {
    key: 'run-ads-audit',
    label: 'Run Ads Audit',
    description: 'AI reviews connected ad accounts and scores performance',
  },
  {
    key: 'generate-strategy-insights',
    label: 'Generate Strategy Insights',
    description: 'AI produces marketing strategy recommendations from practice data',
  },
  {
    key: 'generate-content-plan',
    label: 'Generate Content Plan',
    description:
      'AI builds a 12-week calendar of blogs, social, landing pages, and assets from approved strategies',
  },
  {
    key: 'generate-campaign-recs',
    label: 'Generate Campaign Specific Recommendations',
    description:
      'AI reviews all active campaigns (30+ days) and generates budget, headline, keyword, and scheduling recommendations',
  },
];

/**
 * `actions` narrows the list to those keys (in AI_ACTIONS order) so a module
 * can show only the runs that belong to it. `locationId` pins the card to one
 * location and hides the picker. Without either it is the full Admin card.
 */
export function RunActionsCard({
  practiceId,
  locations,
  actions,
  locationId,
  title = 'Run an action',
}: {
  practiceId: string;
  locations: Location[];
  actions?: readonly string[];
  locationId?: string;
  title?: string;
}) {
  const toast = useToast();
  const [pickedLocation, setSelectedLocation] = useState(
    locations.find((l) => l.isDefault)?.id ?? locations[0]?.id ?? '',
  );
  const selectedLocation = locationId ?? pickedLocation;
  const visibleActions = actions ? AI_ACTIONS.filter((a) => actions.includes(a.key)) : AI_ACTIONS;
  const [runningAction, setRunningAction] = useState<string | null>(null);
  // Last outcome per action, kept on the row. A toast is gone in four seconds;
  // "queued 3 minutes ago" is the thing you actually need to still be able to read.
  const [results, setResults] = useState<Record<string, AiActionResult>>({});

  const handleRun = async (action: AiAction) => {
    if (!selectedLocation) {
      toast.error('Select a location first.');
      return;
    }
    setRunningAction(action.key);
    try {
      const result = await agencyApi.runAiAction(practiceId, action.key, selectedLocation);
      setResults((prev) => ({ ...prev, [action.key]: result }));
      // Report what the backend said happened. Saying "completed" for a job
      // that was merely enqueued — or one that declined to do anything — is
      // what made these buttons look broken.
      if (result.outcome === 'done') toast.success(result.message);
      else if (result.outcome === 'queued') toast.info(result.message);
      else toast.warning(result.message);
    } catch (err) {
      const message = (err as Error).message;
      setResults((prev) => ({
        ...prev,
        [action.key]: { outcome: 'noop', message: `Failed: ${message}` },
      }));
      toast.error(`${action.label} failed: ${message}`);
    } finally {
      setRunningAction(null);
    }
  };

  if (locations.length === 0 && !locationId) return null;

  return (
    <Card title={title}>
      {locationId === undefined && (
        <div className={styles.aiSelect}>
          <Select
            label="Location"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            options={locations.map((l) => ({
              value: l.id,
              label: l.name + (l.isDefault ? ' (Primary)' : ''),
            }))}
          />
        </div>
      )}
      {/* The audit strip belongs to the site-audit actions; skip it when they are filtered out. */}
      {visibleActions.some((a) => a.key === 'recrawl-site' || a.key === 'run-site-audit') && (
        <AuditStatusStrip practiceId={practiceId} locationId={selectedLocation} />
      )}
      <div className={styles.aiActions}>
        {visibleActions.map((action) => {
          const result = results[action.key];
          return (
            <div key={action.key} className={styles.aiActionRow}>
              <div>
                <div className={styles.aiActionLabel}>{action.label}</div>
                <div className={styles.aiActionDesc}>{action.description}</div>
                {result && (
                  <div className={clsx(styles.outcome, styles[result.outcome])}>
                    <span className={styles.outcomeTag}>{OUTCOME_LABEL[result.outcome]}</span>
                    {result.message}
                  </div>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                loading={runningAction === action.key}
                disabled={runningAction !== null}
                onClick={() => handleRun(action)}
              >
                Run
              </Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * Admin controls for per-practice feature experiences — each one wipes a
 * feature's data for a location so the practice can start it from scratch
 * (undoing a skipped or erroneous onboarding, or clearing pilot/test traffic).
 */
export function FeatureResetsCard({
  practiceId,
  locations,
  only,
  locationId,
}: {
  practiceId: string;
  locations: Location[];
  /** Render just one feature's reset row (titled "Reset"); default renders both. */
  only?: 'reviews' | 'safety';
  /** Pin the card to one location and hide the picker. */
  locationId?: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pickedLocation, setSelectedLocation] = useState(
    locations.find((l) => l.isDefault)?.id ?? locations[0]?.id ?? '',
  );
  const selectedLocation = locationId ?? pickedLocation;
  const showSafety = only === undefined || only === 'safety';
  const showReviews = only === undefined || only === 'reviews';
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewConfirmOpen, setReviewConfirmOpen] = useState(false);

  const resetMutation = useMutation({
    mutationFn: () => agencyApi.resetSafetyExperience(practiceId, selectedLocation),
    onSuccess: (res) => {
      const c = res.data;
      toast.success(
        `Safety & Training reset — removed ${c.completions} completions, ${c.equipment} equipment logs. Staff records are kept. The practice will see the setup screen again.`,
      );
      setConfirmOpen(false);
    },
    onError: () => toast.error('Reset failed — try again.'),
  });

  const resetReviewMutation = useMutation({
    mutationFn: () => agencyApi.resetReviewAccelerator(practiceId, selectedLocation),
    onSuccess: (res) => {
      const c = res.data;
      // The Command Center's funnel drawer reads the rows this just deleted.
      queryClient.invalidateQueries({ queryKey: commandCenterKeys.all });
      toast.success(
        `Review Accelerator reset — removed ${c.invites} invites, ${c.funnelEvents} funnel events, ${c.submissions} patient submissions. Funnel stats and the Get reviews counters are back to zero.`,
      );
      setReviewConfirmOpen(false);
    },
    onError: () => toast.error('Reset failed — try again.'),
  });

  if (locations.length === 0) return null;
  const locationName = locations.find((l) => l.id === selectedLocation)?.name ?? '';

  return (
    <Card title={only ? 'Reset' : 'Feature Experiences'}>
      {locationId === undefined && (
        <div className={styles.aiSelect}>
          <Select
            label="Location"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            options={locations.map((l) => ({
              value: l.id,
              label: l.name + (l.isDefault ? ' (Primary)' : ''),
            }))}
          />
        </div>
      )}
      <div className={styles.aiActions}>
        {showSafety && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>Reset Safety &amp; Training</div>
              <div className={styles.aiActionDesc}>
                Wipes the setup, training assignments, completions &amp; e-signatures, equipment
                logs, and the Exposure Control Plan for this location. The staff roster is kept. The
                practice starts the experience from scratch.
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              disabled={!selectedLocation}
              onClick={() => setConfirmOpen(true)}
            >
              Reset
            </Button>
          </div>
        )}
        {showReviews && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>Reset Review Accelerator</div>
              <div className={styles.aiActionDesc}>
                Zeroes the funnel stats in the Command Center dashboard and the scan / opened /
                completed counters on the practice&apos;s Get reviews tab, and clears every patient
                submission captured through the QR flow. Use it to wipe pilot or test traffic. The
                QR code, review link and review settings are kept — only captured data is deleted.
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              disabled={!selectedLocation}
              onClick={() => setReviewConfirmOpen(true)}
            >
              Reset
            </Button>
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => resetMutation.mutate()}
        title="Reset Safety & Training?"
        message={`This permanently deletes all Safety & Training data for ${locationName} — including signed training records and equipment log history. The practice will start again from the setup screen. This cannot be undone.`}
        confirmLabel="Reset experience"
        variant="destructive"
        isLoading={resetMutation.isPending}
      />
      <ConfirmDialog
        isOpen={reviewConfirmOpen}
        onClose={() => setReviewConfirmOpen(false)}
        onConfirm={() => resetReviewMutation.mutate()}
        title="Reset Review Accelerator?"
        message={`This permanently deletes every review invite, funnel event and patient submission for ${locationName} — including private patient feedback the practice has not read yet. Funnel stats and capture counters restart at zero. This cannot be undone.`}
        confirmLabel="Reset funnel & counters"
        variant="destructive"
        isLoading={resetReviewMutation.isPending}
      />
    </Card>
  );
}

/**
 * Per-practice feature flags — rarely-needed capabilities the operator opts a
 * practice into. Off by default; each toggle stores a boolean on the practice.
 */
export function FeatureFlagsCard({
  practiceId,
  flags,
  only,
  title = 'Feature flags',
}: {
  practiceId: string;
  flags: Record<string, boolean>;
  /** `ads` renders the two campaign-deletion switches, `insurance` just that one, `reviews` the text
   *  review requests switch (PRD 5 §4.7), `safety` the Safety & Training switch; default renders all. */
  only?: 'ads' | 'insurance' | 'reviews' | 'safety';
  title?: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const showAds = only === undefined || only === 'ads';
  const showInsurance = only === undefined || only === 'insurance';
  const showReviews = only === undefined || only === 'reviews';
  const showSafety = only === undefined || only === 'safety';
  const draftDeletion = flags.campaignDraftDeletion === true;
  const insuranceVerification = flags.insuranceVerification === true;
  const legacyInsurance = flags.legacyInsuranceVerification === true;
  const reviewsSms = flags.reviewsSms === true;
  const forceDelete = flags.campaignForceDelete === true;
  const safetyTraining = flags.safetyTraining === true;

  const mutation = useMutation({
    mutationFn: ({
      key,
      next,
    }: {
      key:
        | 'campaignDraftDeletion'
        | 'insuranceVerification'
        | 'legacyInsuranceVerification'
        | 'campaignForceDelete'
        | 'reviewsSms'
        | 'safetyTraining';
      next: boolean;
    }) => agencyApi.updatePracticeFlags(practiceId, { [key]: next }),
    onSuccess: (_res, { key, next }) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      // FeatureRoute and the sidebar read this cache; without it the section stays hidden for a minute.
      queryClient.invalidateQueries({ queryKey: practiceFeatureFlagKeys.flags(practiceId) });
      const label =
        key === 'insuranceVerification'
          ? 'Insurance Verification'
          : key === 'legacyInsuranceVerification'
            ? 'OLD Insurance Verification'
            : key === 'campaignForceDelete'
              ? 'Force-delete of launched campaigns'
              : key === 'reviewsSms'
                ? 'Text review requests'
                : key === 'safetyTraining'
                  ? 'Safety & Training'
                  : 'Draft deletion';
      toast.success(`${label} ${next ? 'enabled' : 'disabled'} for this practice.`);
    },
    onError: () => toast.error('Could not update the flag — try again.'),
  });

  return (
    <Card title={title}>
      <div className={styles.aiActions}>
        {showAds && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>Allow deleting campaign drafts</div>
              <div className={styles.aiActionDesc}>
                Shows a Delete action on campaigns that exist only in PracticeNova — never launched
                to Google or Meta. Off by default. A campaign that is live or launching on a
                platform can never be deleted this way.
              </div>
            </div>
            <Button
              variant={draftDeletion ? 'secondary' : 'primary'}
              size="sm"
              loading={mutation.isPending && mutation.variables?.key === 'campaignDraftDeletion'}
              onClick={() =>
                mutation.mutate({ key: 'campaignDraftDeletion', next: !draftDeletion })
              }
            >
              {draftDeletion ? 'Disable' : 'Enable'}
            </Button>
          </div>
        )}
        {showInsurance && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>Insurance Verification</div>
              <div className={styles.aiActionDesc}>
                Shows the Operations › Insurance Verification screens (electronic eligibility via
                Stedi and Open Dental write-back) for this practice. Off by default. The practice
                sees it after their next page load.
              </div>
            </div>
            <Button
              variant={insuranceVerification ? 'secondary' : 'primary'}
              size="sm"
              loading={mutation.isPending && mutation.variables?.key === 'insuranceVerification'}
              onClick={() =>
                mutation.mutate({ key: 'insuranceVerification', next: !insuranceVerification })
              }
            >
              {insuranceVerification ? 'Disable' : 'Enable'}
            </Button>
          </div>
        )}
        {showInsurance && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>OLD Insurance Verification (legacy)</div>
              <div className={styles.aiActionDesc}>
                Also shows the earlier call-based screens (Operations › OLD Insurance Verification:
                Practice Credentials and Payers). Kept for testing only — off by default. Has no
                effect unless Insurance Verification above is enabled.
              </div>
            </div>
            <Button
              variant={legacyInsurance ? 'secondary' : 'primary'}
              size="sm"
              loading={
                mutation.isPending && mutation.variables?.key === 'legacyInsuranceVerification'
              }
              onClick={() =>
                mutation.mutate({ key: 'legacyInsuranceVerification', next: !legacyInsurance })
              }
            >
              {legacyInsurance ? 'Disable' : 'Enable'}
            </Button>
          </div>
        )}
        {showReviews && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>Text review requests (SMS)</div>
              <div className={styles.aiActionDesc}>
                Adds “Text after the visit” to Get reviews on this practice's Reviews page, next to
                the link & QR code, so it can text patients after confirmed visits. Off by default.
                Texts still need a sending number and carrier registration before any go out.
                Turning this off hides the option and stops new texts immediately; open
                conversations still answer STOP and HELP.
              </div>
            </div>
            <Button
              variant={reviewsSms ? 'secondary' : 'primary'}
              size="sm"
              loading={mutation.isPending && mutation.variables?.key === 'reviewsSms'}
              onClick={() => mutation.mutate({ key: 'reviewsSms', next: !reviewsSms })}
            >
              {reviewsSms ? 'Disable' : 'Enable'}
            </Button>
          </div>
        )}
        {showSafety && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>Safety &amp; Training</div>
              <div className={styles.aiActionDesc}>
                Shows the Safety &amp; Training screens (staff training, equipment logs, safety
                binder and Exposure Control Plan) for this practice. Off by default. Turning it off
                hides the module but keeps its data; the practice sees the change after their next
                page load.
              </div>
            </div>
            <Button
              variant={safetyTraining ? 'secondary' : 'primary'}
              size="sm"
              loading={mutation.isPending && mutation.variables?.key === 'safetyTraining'}
              onClick={() => mutation.mutate({ key: 'safetyTraining', next: !safetyTraining })}
            >
              {safetyTraining ? 'Disable' : 'Enable'}
            </Button>
          </div>
        )}
        {showAds && (
          <div className={styles.aiActionRow}>
            <div>
              <div className={styles.aiActionLabel}>Allow force-deleting launched campaigns</div>
              <div className={styles.aiActionDesc}>
                Nuclear option for exception management: shows a Delete action on a{' '}
                <strong>paused</strong> campaign that was launched to Google/Meta. It removes only
                the PracticeNova record and does <strong>nothing</strong> on the platform — the
                campaign must be deleted there separately. Requires typing DELETE to confirm. Off by
                default; a campaign still serving can never be deleted this way.
              </div>
            </div>
            <Button
              variant={forceDelete ? 'secondary' : 'primary'}
              size="sm"
              loading={mutation.isPending && mutation.variables?.key === 'campaignForceDelete'}
              onClick={() => mutation.mutate({ key: 'campaignForceDelete', next: !forceDelete })}
            >
              {forceDelete ? 'Disable' : 'Enable'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * The location's current site analysis.
 *
 * The two audit actions enqueue a job and return, so the request succeeding
 * says nothing about whether the audit ran. This reads the analysis row
 * directly and polls while one is in flight, which is how you tell a queued
 * run from one that failed in the worker ten minutes later.
 */
export function AuditStatusStrip({
  practiceId,
  locationId,
}: {
  practiceId: string;
  locationId: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: agencyKeys.auditStatus(practiceId, locationId),
    queryFn: () => agencyApi.getAuditStatus(practiceId, locationId),
    enabled: !!locationId,
    // Poll only while something is actually running.
    refetchInterval: (query) => (isRunning(query.state.data ?? null) ? 10_000 : false),
    refetchIntervalInBackground: false,
  });

  if (!locationId || isLoading) return null;

  const tone = auditTone(data ?? null);
  return (
    <div className={clsx(styles.auditStrip, styles[tone])}>
      <span className={styles.auditLabel}>Site audit</span>
      <span className={styles.auditState}>{auditSummary(data ?? null)}</span>
    </div>
  );
}

function isRunning(analysis: SiteAuditStatus | null): boolean {
  return analysis?.status === 'pending' || analysis?.status === 'in_progress';
}

function auditTone(analysis: SiteAuditStatus | null): 'queued' | 'done' | 'noop' {
  if (!analysis) return 'noop';
  if (isRunning(analysis)) return 'queued';
  return analysis.status === 'completed' ? 'done' : 'noop';
}

function auditSummary(analysis: SiteAuditStatus | null): string {
  if (!analysis) {
    return 'This site has never been crawled — start with “Re-crawl website & re-audit”.';
  }
  const when = relativeTime(analysis.updatedAt);
  if (isRunning(analysis)) {
    const pages = analysis.pagesFetched ? ` · ${analysis.pagesFetched} pages fetched` : '';
    return `Running — started ${relativeTime(analysis.createdAt)}${pages}`;
  }
  if (analysis.status === 'completed') {
    return `Last completed ${when}${analysis.pagesFetched ? ` · ${analysis.pagesFetched} pages` : ''}`;
  }
  return `Last run ${when} ended as “${analysis.status}”${
    analysis.errorMessage ? ` — ${analysis.errorMessage}` : ''
  }`;
}

/** "3m ago" / "2h ago" / "4d ago". */
function relativeTime(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * Model overrides, per location.
 *
 * The only way to reach these used to be a ghost "Models" button sitting
 * fourth in a row of per-location controls, below the fold of a long page.
 * This surfaces the same modal from a card of its own, and shows at a glance
 * which locations are running an override rather than the defaults.
 */
export function ModelOverridesCard({
  practiceId,
  locations,
}: {
  practiceId: string;
  locations: Location[];
}) {
  const [openLocationId, setOpenLocationId] = useState<string | null>(null);

  if (locations.length === 0) return null;
  const openLocation = locations.find((l) => l.id === openLocationId) ?? null;

  return (
    <Card title="Models">
      <p className={styles.cardIntro}>
        Every prompt runs on the platform default unless a location overrides it. Overrides are set
        per location, and apply to every feature that location uses.
      </p>
      <div className={styles.aiActions}>
        {locations.map((loc) => {
          const count = loc.anthropicModels ? Object.keys(loc.anthropicModels).length : 0;
          return (
            <div key={loc.id} className={styles.aiActionRow}>
              <div>
                <div className={styles.aiActionLabel}>
                  {loc.name}
                  {loc.isDefault && <span className={styles.primaryTag}> Primary</span>}
                </div>
                <div className={styles.aiActionDesc}>
                  {count === 0
                    ? 'Using the platform default for every prompt'
                    : `${count} prompt${count === 1 ? '' : 's'} overridden`}
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setOpenLocationId(loc.id)}>
                {count > 0 ? `Edit (${count})` : 'Set models'}
              </Button>
            </div>
          );
        })}
      </div>
      {openLocation && (
        <ModelOverridesModal
          isOpen
          onClose={() => setOpenLocationId(null)}
          practiceId={practiceId}
          location={openLocation}
        />
      )}
    </Card>
  );
}
